import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CustomButton from 'components/CustomInput/Button';
import CustomTextField from 'components/Input/CustomTextField';
import CustomOutlineSelect from 'components/Input/CustomOutlineSelect';
import { Paper } from '@material-ui/core';
import useDialog from 'components/ConfirmDialog/useDialog';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import DeleteIcon from '@material-ui/icons/Delete';
// import UploadFileInput from 'components/uploadFile/uploadFileInput';
import styled from 'styled-components';
import UploadDisplay from 'components/uploadFile/UploadDisplay';
import { generateRandomId, produce } from 'utility/general';
import UploadWrapper from 'components/UploadWrapper';
import { RETURNED, statusDetailType } from 'utility/lookup';
import { useDispatch } from 'react-redux';
import { UPDATE_APPLICATION_STATUS_WITH_DETAIL, UPDATE_ALL_DRAFT_COMMENT, CCO_RETURN_ACTION } from 'store/key';
import { RETURN_APPLICATION_SUCCESS, TABLE_ROW_REMOVE } from 'utility/frontendMessage';
import { setGlobalLoading } from 'store/global/reducer';
import { useHistory } from 'react-router-dom';

const SItemLayout = styled.div`
    flex-grow: 1;
    padding-left: 10px;
`;

const ReturnCommentDialog = ({
    children,
    // onConfirm = () => null,
    onCancel = () => null,
    isShow = false,
    title = '',
    description = '',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    disableCancelButton = false,
    currentStatus,
    formId,
    applicationDetail,
    commentPermission,
}) => {
    const getDefaultData = () => {
        return {
            key: generateRandomId(),
            recipient: '',
            comment: '',
            uploads: [],
            uploadTempId: [],
        };
    };

    const showDialog = useDialog();
    const [forceErrorDisplay, setForceErrorDisplay] = useState(false);
    const [isUploading, setUploading] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const dispatch = useDispatch();
    const [data, setData] = useState([getDefaultData()]);
    const history = useHistory();
    const [maxLengthValidation, setMaxLengthValidation] = useState({});

    const getDefaultExpireDay = () => {
        const date = new Date();
        date.setDate(date.getDate() + 90);
        return date;
    };

    const [expireDay, setExpireDay] = useState(getDefaultExpireDay());

    const checkValidation = () => {
        let isPass = true;

        data.forEach((item) => {
            if (!item.recipient) {
                isPass = false;
            } else {
                if (!item.comment) {
                    isPass = false;
                }
            }
        });

        Object.values(maxLengthValidation).forEach((item) => {
            if (!item) {
                isPass = false;
            }
        });

        return isPass;
    };

    /** refresh data & expireDay */
    useEffect(() => {
        if (isShow) {
            setData([getDefaultData()]);
            setExpireDay(getDefaultExpireDay());
            setForceErrorDisplay(false);
            setMaxLengthValidation({});
            // setRecipients([]);
        }
        // eslint-disable-next-line
    }, [isShow]);

    /** init recipients when applicationDetail change */
    useEffect(() => {
        if (applicationDetail) {
            let list = [
                {
                    key: 'appId-' + applicationDetail.applicationForm.id,
                    display: applicationDetail.applicationForm.institution + ' - Study Info',
                    type: statusDetailType.STUDY_INFO,
                    value: applicationDetail.applicationForm.id,
                },
            ];

            for (let i of applicationDetail.siteInfo) {
                list.push({
                    key: 'siteId-' + i.id,
                    display: i.institution + ' - Site Info',
                    type: statusDetailType.SITE,
                    value: i.id,
                });
            }
            setRecipients(list);
        }
    }, [applicationDetail]);

    const onFileUpload = (list, key) => {
        setData(
            produce((draft) => {
                const index = draft.findIndex((item) => item.key === key);
                draft[index].uploadTempId = [...draft[index].uploadTempId, ...list.map((item) => item.key)];

                draft[index].uploads = [
                    ...draft[index].uploads,
                    ...list.map((item) => {
                        return { name: item.fileName };
                    }),
                ];
            }),
        );
    };

    const handleConfirm = () => {
        const isValid = checkValidation();
        if (!isValid) {
            setForceErrorDisplay(true);
            return;
        }

        const updatePayload = {
            id: formId,
            status: RETURNED,
            currentStatus,
            details: data
                .map((item) => {
                    const { uploadTempId, comment, type, value } = item;

                    return {
                        comment,
                        uploadTempId,
                        type,
                        value,
                    };
                })
                .filter((item) => item.comment),
            expiryDate: expireDay,
            hasCCOAction: true,
            updateApplicationDetail: true,
            callback: async () => {
                history.push('/');
                await showDialog({
                    description: RETURN_APPLICATION_SUCCESS,
                    disableCancelButton: true,
                });
            },
            final: () => {
                setGlobalLoading(false);
            },
        };

        setGlobalLoading(true);
        dispatch({ type: UPDATE_ALL_DRAFT_COMMENT, payload: formId });

        if (commentPermission) {
            dispatch({
                type: CCO_RETURN_ACTION,
                payload: {
                    applicationFormId: formId,
                    applicationDetail: applicationDetail,
                    callback: () => {
                        dispatch({
                            type: UPDATE_APPLICATION_STATUS_WITH_DETAIL,
                            payload: updatePayload
                        });
                    },
                    final: () => { },
                },
            });
            return;
        }

        dispatch({ type: UPDATE_APPLICATION_STATUS_WITH_DETAIL, payload: updatePayload });
    };

    const getFilterRecipients = () => {
        return recipients.map((item) => {
            const isExist = data.some((dataItem) => dataItem.recipient === item.key);

            if (isExist) {
                item.isDisabled = true;
            } else {
                item.isDisabled = false;
            }

            return item;
        });
    };

    const getContent = () => {
        return (
            <div>
                {data.map((item, rowIndex) => {
                    const isNewComment = !item.recipient;

                    return (
                        <Paper elevation={1} key={item.key} style={{ display: 'flex', marginBottom: 15, padding: 10 }}>
                            <div style={{ width: 550 }}>
                                <div>
                                    <CustomOutlineSelect
                                        value={item.recipient}
                                        label="Recipient"
                                        showEmptyChoice={isNewComment}
                                        choices={getFilterRecipients()}
                                        onChange={(e) => {
                                            // get key of the object
                                            const { value } = e.target;

                                            const currentRecipient = recipients.find((item) => {
                                                return value === item.key;
                                            });

                                            setData(
                                                produce((draft) => {
                                                    draft[rowIndex].recipient = value;
                                                    draft[rowIndex].type = currentRecipient.type;
                                                    draft[rowIndex].value = currentRecipient.value;
                                                }),
                                            );
                                        }}
                                    />
                                </div>
                                <div style={{ marginTop: 10 }}>
                                    <CustomTextField
                                        label={`Leave comment${isNewComment ? ' (*select Recipient first)' : ''}`}
                                        isTextArea
                                        disabled={isNewComment}
                                        value={data[rowIndex].comment}
                                        onChange={(e) => {
                                            const { value } = e.target;

                                            setData(
                                                produce((draft) => {
                                                    draft[rowIndex].comment = value;
                                                }),
                                            );
                                        }}
                                        maxLength={255}
                                        isValidValue={(isValid) => {
                                            setMaxLengthValidation(
                                                produce((draft) => {
                                                    draft[data[rowIndex].key] = isValid;
                                                }),
                                            );
                                        }}
                                        forceErrorDisplay={forceErrorDisplay}
                                        validators={['isRequired']}
                                    />
                                </div>
                            </div>

                            <SItemLayout>
                                <UploadDisplay
                                    fileList={data[rowIndex].uploads}
                                    enableRemove
                                    onRemove={(deleteIndex) => {
                                        setData(
                                            produce((draft) => {
                                                const dataIndex = draft.findIndex(
                                                    (draftItem) => draftItem.key === item.key,
                                                );
                                                draft[dataIndex].uploads.splice(deleteIndex, 1);
                                                draft[dataIndex].uploadTempId.splice(deleteIndex, 1);
                                            }),
                                        );
                                    }}
                                />

                                <UploadWrapper
                                    inputStyle
                                    optional
                                    disabled={isNewComment}
                                    onLoadingChange={(isUploading) => setUploading(isUploading)}
                                    onUploadFailed={() => setUploading(false)}
                                    multiple
                                    onFileUploadFinish={(list) => {
                                        onFileUpload(list, item.key);
                                    }}
                                />
                            </SItemLayout>
                            <div style={{ marginLeft: 10 }}>
                                <DeleteIcon
                                    style={{
                                        cursor: data.length <= 1 ? 'not-allowed' : 'pointer',
                                        color: data.length <= 1 ? '#808080' : '#3F51B5',
                                    }}
                                    onClick={async () => {
                                        if (data.length <= 1) {
                                            return;
                                        }

                                        const isConfirm = await showDialog({
                                            description: TABLE_ROW_REMOVE,
                                        });

                                        if (!isConfirm) {
                                            return;
                                        }

                                        setData(
                                            produce((draft) => {
                                                draft.splice(rowIndex, 1);
                                            }),
                                        );
                                    }}
                                />
                            </div>
                        </Paper>
                    );
                })}
            </div>
        );
    };

    return (
        <Dialog open={isShow} style={{ margin: 'auto' }} maxWidth={'lg'}>
            <DialogTitle style={{ fontWeight: 'bold', width: 900 }}>
                <div style={{ fontSize: 18 }}>{title}</div>
            </DialogTitle>
            <DialogContent>
                <div style={{ display: 'flex' }}>
                    <CustomButton
                        disabled={data.length >= recipients.length || isUploading}
                        onClick={() => {
                            setData([...data, getDefaultData()]);
                        }}
                    >
                        Add Comment
                    </CustomButton>
                </div>
                <div style={{ marginTop: 20 }}>{getContent()}</div>

                <div style={{ marginTop: 30 }}>
                    <CustomDatePicker
                        value={expireDay}
                        label={'Expire Date'}
                        handleChangeDate={(e) => {
                            setExpireDay(e.target.value);
                        }}
                        minDate={new Date().getTime()}
                        forceErrorDisplay={forceErrorDisplay}
                        validators={['isRequired']}
                    />
                </div>
            </DialogContent>
            <DialogActions style={{ padding: 15 }}>
                {!disableCancelButton && (
                    <CustomButton type={'cancel'} onClick={onCancel}>
                        {cancelText ?? 'Cancel'}
                    </CustomButton>
                )}

                <CustomButton disabled={isUploading} onClick={handleConfirm} autoFocus>
                    {confirmText ?? 'Confirm'}
                </CustomButton>
            </DialogActions>
        </Dialog>
    );
};

export default ReturnCommentDialog;
